import { StorefrontAssetSource, StorefrontAssetUsageLocation } from '../../../models/Storefront';

// Shared presentation helpers for the asset screens (table, details panel, scan dialog).

export const formatBytes = (bytes?: number): string => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
};

const USAGE_LOCATION_LABELS: Record<StorefrontAssetUsageLocation, string> = {
    settings: 'Storefront settings',
    globalSettings: 'Global settings',
    banner: 'Banner',
    section: 'Section',
    style: 'Style',
    template: 'Template',
};

export const usageLocationLabel = (location: StorefrontAssetUsageLocation | string): string =>
    USAGE_LOCATION_LABELS[location as StorefrontAssetUsageLocation] ?? location;

// How the asset got here, in words rather than the raw enum value. Shared by the library table's
// Source column and the details panel.
const ASSET_SOURCE_LABELS: Record<StorefrontAssetSource, string> = {
    upload: 'Uploaded',
    scan: 'Found by scan',
    template: 'From template',
};

export const assetSourceLabel = (source: StorefrontAssetSource | string | undefined): string =>
    source ? (ASSET_SOURCE_LABELS[source as StorefrontAssetSource] ?? source) : '';

// Pull a human-readable reason out of whatever an RTK Query mutation rejected with: a
// FetchBaseQueryError ({ status, data }) whose data is usually an ActionResponse { message }, or a
// plain Error.
export const extractErrorMessage = (e: unknown): string | undefined => {
    if (typeof e === 'object' && e !== null) {
        const err = e as { status?: unknown; data?: { message?: unknown; title?: unknown } };
        const message = err.data?.message ?? err.data?.title;
        if (typeof message === 'string' && message) return message;
    }
    if (e instanceof Error) return e.message || undefined;
    return undefined;
};

// The last path segment of a blob path, enough to recognise a file without the full prefix.
export const blobPathTail = (blobPath: string): string => {
    const parts = (blobPath ?? '').split('/');
    return parts[parts.length - 1] || blobPath;
};
